import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CODE_LENGTH = 6;

const TwoFactorAuth: React.FC = () => {
  const { verifyTwoFactor, user, isLoading, error, logout } = useAuth();

  const [code, setCode] = useState<string[]>(
    Array(CODE_LENGTH).fill('')
  );

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) {
        return;
      }

      const newCode = [...code];
      newCode[index] = value.slice(-1);

      setCode(newCode);

      if (value && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code]
  );

  const handleSubmit = useCallback(async () => {
    const fullCode = code.join('');

    if (fullCode.length !== CODE_LENGTH) {
      return;
    }

    if (!user) {
      return;
    }

    await verifyTwoFactor({
      userId: user.id,
      code: fullCode,
    });
  }, [code, user, verifyTwoFactor]);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
      if (e.key === 'Enter') void handleSubmit();
    },
    [code, handleSubmit]
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
        {/* Cabeçalho */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
            <Shield className="text-emerald-600" size={32} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Verificação em duas etapas
          </h1>

          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Digite o código atual do aplicativo autenticador de {user?.email}.
          </p>
        </div>

        {/* Código */}
        <div className="flex justify-center gap-3 mb-6">
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={code[index]}
              onChange={(e) =>
                handleChange(index, e.target.value)
              }
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 outline-none transition"
            />
          ))}
        </div>

        {/* Erro */}
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Botão */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full mb-4 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
        >
          {isLoading ? 'Verificando...' : 'Verificar código'}
        </button>

        {/* Logout */}
        <button
          type="button"
          onClick={logout}
          className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
        >
          Voltar ao login
        </button>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
